import { reactive, ref } from 'vue';
import type { Reactive, Ref } from 'vue';
import mime from 'mime';
import { ENDPOINTS, skapi } from '@/main';
import { resolveRegion } from './regions';
import { formatSpecs } from '@/code/service-spec';
import { suspensionClock, cancelScheduled } from '@/code/plan-state';
import { dateFormat } from '@/code/utils';
import { DatabaseResponse, SkapiError } from 'skapi-js';
// One implementation of "which url does a cacheable mint use", shared with the
// BunnyQuery widget so the two cannot drift into different cache behaviour.
import { previewMintCacheToken } from 'bunnyquery/engine';
import { normalizeStoragePath } from '@/code/utils';

const ETC_CONFIG = JSON.parse(import.meta.env.VITE_ETC) as {
	hostDomain: string;
	target_cdn: string;
};
export const hostDomain = ETC_CONFIG?.hostDomain || 'skapi.com';
/**
 * The storage CDN this environment reads from. Same distribution the SDK takes
 * `<short_region>/admin-v2.json` off, and the default matches skapi-js's own
 * `target_cdn` fallback (main/skapi.ts) so dev and prod resolve exactly the way
 * the SDK does. Used to fetch the published BunnyQuery widget template; see
 * code/widget_update.ts.
 */
export const storageCdnDomain = `https://${ETC_CONFIG?.target_cdn || 'd3e9syvbtso631'}.cloudfront.net`;
/**
 * The `__ETC__` literal a hosted widget page must be rendered with: the same
 * skapi-js extra options this dashboard itself runs on, so a page built here
 * points at the same environment. Mirrors ETC_LITERAL_PROD / ETC_LITERAL_DEV in
 * infra/_bunnyquery_widget_update.py.
 */
export const widgetEtcLiteral = JSON.stringify(ETC_CONFIG ?? null);
export const product_price_code = JSON.parse(import.meta.env.VITE_PRODUCT);

/**
 * The access group the project's AI client-secret key must be registered under.
 *
 * `client_secret_key_request` resolves a key by looking it up and then refusing
 * an unauthenticated caller outright when the row's group is non-zero
 * (`if grp != 0 and not auth`). So the key's group is what decides whether a
 * visitor with no account can send a chat message at all, and it has to track
 * the project's "Require login" setting rather than being pinned.
 *
 * Group 0 is not a weaker secret: the key value is never handed to the browser
 * either way, it is substituted into the upstream request server side. What it
 * does mean is that anyone who knows the project id can spend the owner's model
 * budget, which is inherent to serving a chat with no login and is exactly what
 * the owner opts into by turning the login requirement off.
 *
 * Reads `require_login`, NOT `prevent_anonymous`. The two are unrelated:
 * prevent_anonymous governs anonymous record WRITES at the skapi layer, which
 * the BunnyQuery MCP refuses unconditionally anyway.
 *
 * Defaults to 1 (locked) for anything but an explicit `require_login: false`, so
 * a service record that has not loaded, or predates the flag, stays closed.
 */
export function aiClientSecretGrp(service: { service?: { require_login?: boolean } }): number {
	return service?.service?.require_login === false ? 0 : 1;
}

/**
 * True for a db-storage entry that is system-managed and must not be shown in
 * the user's file browser.
 *
 * Reserved names are SHOUTY double-underscored (`__NAME__`) — currently just the
 * MCP export namespace `__MCP_EXPORTS__`, which holds transient large-query
 * exports that the MCP server writes and ages out on its own 1-day TTL.
 * `mcp-exports` is the pre-rename name and is matched so already-written
 * exports stay hidden too (they are deliberately not migrated: a download link
 * bakes in its object key, so moving them would break live links).
 *
 * Folders arrive from the backend as `#name`, hence the leading-`#` strip.
 * NOTE: this is UI decluttering, not a permission boundary — a reserved path is
 * still reachable if addressed directly.
 */
export function isReservedDbEntry(name: unknown): boolean {
	if (typeof name !== 'string' || !name) return false;
	const raw = name[0] === '#' ? name.slice(1) : name;
	return /^__.+__$/.test(raw) || raw === 'mcp-exports';
}

export type ServiceFromServer = {
	service: string;
	owner: string;

	name: string; // service name
	cors: string; // http://example.com,https://example.com
	created_locale: string; // country code ex) US. Unchangable after creation.
	description?: string; // service description
	region: string; // aws region code like "us-west-1"

	subs_id: string; // stripe subscription id
	timestamp: number; // created 13 digit timestamp

	active: number; // 1 running, 0 disabled, -1 suspended, -2 deleted, below -100 contained
	group:
		| 1 // "Trial", // currently shown as Standard
		| 2 // "Standard",
		| 3 // "Premium",
		| 50 // "Unlimited",
		| 51 // "Standard (Perpetual License)",
		| 52 // "Premium (Perpetual License)",
		| 61 // "Standard (Promo)", // reverts to 1(Trial) after one month
		| 63 // "Premium (Promo)", // reverts to 1(Trial) after three months
		| 64; // "Premium (Promo)", // reverts to 1(Trial) after six months

	email: string; // owners email

	subdomain: string; // only the subdomain part, not the full url

	users: number; // number of users in this service
	newsletter_subscribers: number; // number of public newsletter subscribers

	email_triggers: {
		template_setters: {
			welcome: string;
			confirmation: string;
			newsletter_subscription: string;
			verification: string;
			invitation: string;
			/** the Password Reset Email's setter; absent from a region whose listing predates the kind */
			forgot_password?: string;
		};
	};

	storage_info: {
		database: number;
		cloud: number;
		email: number;
		host: number;
		file: number;
		// Current UTC month counters, joined in by pull-services from the
		// storage row's `<metric>_YYYYMM` attributes. Optional: a region whose
		// pull-services predates them sends none, and the meters must not NaN.
		egress?: number; // bytes served this month
		email_sends?: number; // newsletter mails accepted by SES this month
		mau?: number; // distinct users that produced a token this month
		month?: string; // "YYYY-MM", the month the three counters belong to
	};

	// Overage summary written onto the service row by the OverageMeter job for
	// every service in an overage group. Absent on hard stop plans and before the
	// job's first run; the dashboard reads it as "No overage recorded yet".
	ovg?: ServiceOverage;

	vapid_private_key?: string;
	vapid_public_key?: string;
	api_key: string; // deprecated

	host: string;

	projectId?: string;

	email_alias?: string;
	prevent_signup?: boolean;
	prevent_inquiry?: boolean;
	freeze_database?: boolean;
	prevent_anonymous?: boolean;
	/** BunnyQuery: does the embeddable chat require an account? Defaults to true. */
	require_login?: boolean;
	ai_agent?: string;
	/**
	 * The phone verification SMS text (verifyPhoneNumber), with ${service_name} and ${code}, at most
	 * 160 characters. Absent or '' when the built-in default is sent.
	 */
	template_sms_verification?: string;
	suspended_at_string?: string;
	suspended_at?: number; // 13 digit timestamp, stamped when active became -1
	// Plan policy v2 suspension attributes, written by the quota evaluator and
	// removed in the same write that moves active off -1. A -1 row without
	// suspend_reason is a legacy or manual suspension.
	suspend_reason?: string; // "quota"
	suspend_over?: string; // comma list drawn from dbst, fpool, emlst (see plan-state suspendOverFromRow)
	egrblk?: string; // "YYYYMM": bandwidth refused that month (only while ENFORCE_EGRESS_HARD_STOP is on)

	client_secret?: { [key: string]: string }; // to be deprecated, moving to separate db
	auth_client_secret?: string[]; // to be deprecated, moving to separate db

	// -- CUSTOM ADDED FIELDS (not from server) --
	will_terminate?: string; // date string of the EARLIEST deletion of a suspended project (suspended_at + 30 days); '' when not suspended, undated, or held by a subscription
};

/** The `ovg` map on a service row, exactly as the OverageMeter job writes it.
 *  `metric` keys are fpool, dbst, emlst, egres, emlsd, mau (and `users` in
 *  `use`/`lim`, which is what mau is measured against). A null limit means
 *  unlimited. Money is integer cents. */
export type ServiceOverage = {
	prd: string; // "YYYY-MM"
	ts: number; // ms of the last sample
	grp: number;
	hrdstp: boolean;
	billable: boolean; // plan allows overage AND a subscription is attached
	use: { [metric: string]: number };
	lim: { [metric: string]: number | null };
	// billed false: metered but not charged (cents is then 0). Absent = billed.
	ovg: { [metric: string]: { units: number; cents: number; billed?: boolean } };
	tot_cents: number;
	rates: { [metric: string]: { cents_per_unit: number; unit: string; billed?: boolean } };
	bill: { status: string; ts: number };
};

export type SubscriptionObj = {
	id: string;
	object: string;
	application: string | null;
	application_fee_percent: number | null;
	automatic_tax: {
		enabled: boolean;
		liability: string | null;
	};
	billing_cycle_anchor: number;
	billing_cycle_anchor_config: object | null;
	billing_thresholds: object | null;
	cancel_at: number | null; // actually cancelled at (10 digit timestamp)
	cancel_at_period_end: boolean;
	canceled_at: number | null; // requested cancel at (10 digit timestamp)
	cancellation_details: {
		comment: string | null;
		feedback: string | null;
		reason: string | null;
	};
	collection_method: string;
	created: number;
	currency: string;
	current_period_end: number;
	current_period_start: number;
	customer: string;
	days_until_due: number | null;
	default_payment_method: string | null;
	default_source: string | null;
	default_tax_rates: string[];
	description: string | null;
	discount: object | null;
	discounts: object[];
	ended_at: number | null;
	invoice_settings: {
		account_tax_ids: string | null;
		issuer: {
			type: string;
		};
	};
	items: object;
	latest_invoice: string;
	livemode: boolean;
	metadata: object;
	next_pending_invoice_item_invoice: string | null;
	on_behalf_of: string | null;
	pause_collection: object | null;
	payment_settings: object;
	pending_invoice_item_interval: object | null;
	pending_setup_intent: string | null;
	pending_update: object | null;
	plan: object;
	quantity: number;
	schedule: object | null;
	start_date: number;
	status: string;
	test_clock: object | null;
	transfer_data: object | null;
	trial_end: number | null;
	trial_settings: object;
	trial_start: number | null;

	// -- CUSTOM ADDED FIELDS (not from server) --
	will_cancel?: string; // date string of when the subscription will be cancelled after cancellation request
};

function randomString(length: number) {
	let result = '';
	let characters =
		'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
	let charactersLength = characters.length;
	for (let i = 0; i < length; i++) {
		result += characters.charAt(
			Math.floor(Math.random() * charactersLength),
		);
	}
	return result;
}

export class Service {
	id: string;
	owner: string;
	admin_private_endpoint: string;
	record_private_endpoint: string;
	record_public_endpoint: string;
	admin_public_endpoint: string;
	service: ServiceFromServer;
	dateCreated: string;
	subscription: SubscriptionObj;

	_subsPromise;
	newsletterSender: Promise<string>[] = [];
	reserved_key: string = randomString(16);
	formattedSpecs: {
		currentPlan: {
			price: number;
			users_string: string;
			storage: {
				database: string;
				cloud: string;
				email: string;
				host: string;
				file: string;
				pool: string;
			};
			storage_bytes: {
				pool: number;
				database: number;
				cloud: number;
				host: number;
				email: number;
				file: number;
			};
			description: string;
			label: string;
			users: number;
			offer_list: Array<{
				class: string;
				text: string;
			}>;
			// Authored per plan, then overwritten by the served hrdstp flag.
			// false = usage past a limit is billed, true = uploads and sends stop.
			hard_stop?: boolean;
			egress?: string;
			egress_bytes?: number;
			email_sends?: number | null;
			email_sends_string?: string;
		};
		serviceStatus: {
			id: string;
			original_service_id: string;
			statusCode: number;
			name: string;
			status: string;
			canChangePlan: boolean;
			users_string: string;
			users_percent: string;
			users: number;
			newsletter_subscribers_string: string;
			newsletter_subscribers: number;
			storage_bytes: {
				database?: number;
				cloud?: number;
				email?: number;
				host?: number;
				file?: number;
			};
			storage: {
				database: string;
				database_percent: string;
				cloud: string;
				cloud_percent: string;
				email: string;
				email_percent: string;
				email_bytes: number;
				email_limit_zero: boolean;
				host: string;
				host_percent: string;
				pool: string;
				pool_bytes: number;
				pool_percent: string;
				segments: {
					host: { bytes: number; text: string; width: string };
					cloud: { bytes: number; text: string; width: string };
					file: { bytes: number; text: string; width: string };
				};
				file: string;
				file_percent: string;
			};
			// This month's metered counters against the plan's monthly limits.
			usage_month: {
				month: string;
				egress: string;
				egress_percent: string;
				email_sends: string;
				email_sends_percent: string;
				mau: string;
			};
			// Derived from service.ovg; null until the OverageMeter job has run.
			overage: {
				month: string;
				sampled: string;
				billable: boolean;
				bill_status: string;
				total: string;
				rows: Array<{
					metric: string;
					label: string;
					used: string;
					included: string;
					over: string;
					units: string;
					cost: string;
				}>;
			} | null;
		};
	};

	constructor(service: ServiceFromServer, endpoints: string[]) {
		this.id = service.service;
		this.admin_private_endpoint = endpoints[0];
		this.record_private_endpoint = endpoints[1];
		this.admin_public_endpoint = endpoints[2];
		this.record_public_endpoint = endpoints[3];

		this.owner = service.owner;
		this.dateCreated =
			typeof service.timestamp === 'string'
				? service.timestamp
				: new Date(service.timestamp).toDateString();

		service.projectId = skapi.util.formatServiceId(
			this.id,
			this.owner,
		);

		this.service = service;

		// NOTE: subdomain info is NOT fetched here. It's a per-project related-data
		// fetch used only by the service views, so firing it in the ctor hit the
		// backend once for EVERY project on the list. It's now fetched on visit
		// (main.vue run() → getSubdomainInfo). The refresh/getInfo path still
		// re-fetches it. getSubdomainInfo is idempotent (getSubdomainInfoTimer guard).

		this.formattedSpecs = formatSpecs(service);
		// NOTE: the per-project Stripe subscription is NOT fetched here anymore.
		// The ctor fired one api.stripe.com request per listed project, and the
		// SDK's shared request queue drains in whole cycles — so entering a
		// project right after the list loaded queued its pull-services /
		// listClientSecrets behind the slowest Stripe call. It's now triggered
		// via ensureStripeSubscription(), deferred by addServiceToList.
	}

	/**
	 * Memoized trigger for the per-project Stripe subscription fetch. Safe to
	 * call any number of times; the first call starts the fetch, later calls
	 * return the same promise.
	 */
	ensureStripeSubscription(): Promise<SubscriptionObj> {
		if (!this._subsPromise) {
			this._subsPromise = this.getStripeSubscription();
		}
		return this._subsPromise;
	}

	/**
	 * Optimistically adjust the displayed db-file storage usage by `deltaBytes`
	 * (positive for a newly uploaded file, negative for a delete) and rebuild
	 * formattedSpecs so the "Usage" figure updates immediately — the server only
	 * refreshes it on a full refresh()/reload. The next authoritative refresh()
	 * overwrites storage_info.file, so any estimate self-corrects. Do NOT call
	 * for EXISTS uploads — a deduped file stores no new bytes.
	 */
	adjustFileStorageUsage(deltaBytes: number) {
		if (!Number.isFinite(deltaBytes) || deltaBytes === 0) return;
		if (!this.service?.storage_info) return;
		const current = this.service.storage_info.file || 0;
		this.service.storage_info.file = Math.max(0, current + deltaBytes);
		this.formattedSpecs = formatSpecs(this.service);
	}

	/**
	 * Same idea for the user tally: the count on the service row is kept by a
	 * backend stream and only reaches this instance on a full reload, so the Users
	 * page moves it by what it just created or deleted. An invitation is not a
	 * user until it is accepted and never calls this.
	 */
	adjustUserCount(delta: number) {
		if (!Number.isFinite(delta) || delta === 0) return;
		if (!this.service) return;
		this.service.users = Math.max(0, (Number(this.service.users) || 0) + delta);
		this.formattedSpecs = formatSpecs(this.service);
	}

	async registerSenderEmail(params: {
		email_alias: string;
		check?: boolean;
	}): Promise<
		| 'SUCCESS: Sender e-mail has been registered.'
		| 'SUCCESS: E-mail alias is available.'
	> {
		let emailAlias: string;

		if (params && 'email_alias' in params) {
			emailAlias = params.email_alias;
		} else {
			emailAlias = '';
		}

		if (!emailAlias) {
			throw 'Email is required.';
		}

		const specialCharPattern = /[!#$%^&*(),?":{}|<>]/g;
		if (specialCharPattern.test(emailAlias)) {
			throw 'Email contains special characters.';
		}

		let res = await skapi.util.request(
			this.admin_private_endpoint + 'register-sender-email',
			Object.assign(
				{ service: this.id, owner: this.owner },
				{ email_alias: emailAlias, check: params?.check || false },
			),
			{ auth: true },
		);

		if (!params?.check) {
			this.service.email_alias = emailAlias;
		}

		return res;
	}

	registerOpenIDLogger(params: {
		url: string; // openid token 해석 url
		mthd: string; // GET or POST
		hder: { [key: string]: string }; // header
		prms: { [key: string]: string }; // params
		data: any; // post body data (post 만 해당)
		id: string; // openid logger id
		usr: string; // user id로 사용할 openid profile attribute
		cdtn: {
			key: string; // openid profile attribute
			value: string | boolean | number; // openid profile attribute value
			condition: '=' | '>' | '<' | '>=' | '<=' | '!=' | 'ends_with';
			range: string | boolean | number; // value range
		};
		req: 'create' | 'delete' | 'update' | 'list'; // request type
	}) {
		return skapi.util.request(
			this.admin_private_endpoint + 'register-openid',
			Object.assign(
				{ service: this.id, owner: this.owner },
				skapi.util.extractFormData(params).data || {},
			),
			{ auth: true },
		);
	}

	async masterCreateAccount(
		form: any
	) {
		return skapi.createAccount(
			Object.assign({ service: this.id, owner: this.owner }, form)
		);
	}

	async inviteUser(
		form,
		options?: {
			confirmation_url?: string;
			email_subscription?: boolean;
		},
	): Promise<'SUCCESS: Invitation has been sent.'> {
		return skapi.inviteUser(
			Object.assign({ service: this.id, owner: this.owner }, form),
			options,
		);
	}

	async updateUserAttribute(form: { [key: string]: any }): Promise<{ [key: string]: any }> {
		let attributes = skapi.util.extractFormData(form).data;

		await skapi.util.request(
			this.admin_private_endpoint + 'admin-edit-profile',
			Object.assign({ service: this.id, owner: this.owner }, attributes),
			{ auth: true },
		);

		return attributes;
	}

	async grantAccess(params: {
		user_id: string;
		access_group: number;
	}): Promise<'SUCCESS: Access has been granted to the user.'> {
		return skapi.grantAccess(
			Object.assign({ service: this.id, owner: this.owner }, params),
		);
	}

	// get newsletter mail address
	async requestNewsletterSender(params: { group: number }): Promise<string> {
		if (this.newsletterSender[params.group]) {
			return await this.newsletterSender[params.group];
		}

		let sndr = skapi.util.request(
			this.admin_private_endpoint + 'request-newsletter-sender',
			Object.assign(
				{ service: this.id, owner: this.owner },
				skapi.util.extractFormData(params).data || {},
			),
			{ auth: true },
		);
		this.newsletterSender[params.group] = sndr;
		return await sndr;
	}

	async deleteNewsletter(params: { message_id: string; group: number }) {
		return skapi.util.request(
			this.admin_private_endpoint + 'delete-newsletter',
			Object.assign(
				{ service: this.id, owner: this.owner },
				skapi.util.extractFormData(params).data || {},
			),
			{ auth: true },
		);
	}

	async blockAccount(
		userId: string,
	): Promise<'SUCCESS: The user has been blocked.'> {
		await skapi.blockAccount(
			Object.assign(
				{ service: this.id, owner: this.owner },
				{ user_id: userId },
			),
		);
		return 'SUCCESS: The user has been blocked.';
	}

	async unblockAccount(
		userId: string,
	): Promise<'SUCCESS: The user has been unblocked.'> {
		await skapi.unblockAccount(
			Object.assign(
				{ service: this.id, owner: this.owner },
				{ user_id: userId },
			),
		);
		return 'SUCCESS: The user has been unblocked.';
	}

	async deleteAccount(
		userId: string,
	): Promise<'SUCCESS: Account has been deleted.'> {
		await skapi.deleteAccount(
			Object.assign(
				{ service: this.id, owner: this.owner },
				{ user_id: userId },
			),
		);
		return 'SUCCESS: Account has been deleted.';
	}

	//send invitation email, when accepted, user will have their account created, and be redirected
	// async resendInvitation(params: { email: string; redirect: string }): Promise<'SUCCESS: Invitation E-Mail has been sent.'> {
	//   let p: any = skapi.util.extractFormData(params).data;
	//   let resend = await skapi.util.request(
	//     'confirm-signup',
	//     {
	//       service: this.id,
	//       owner: this.owner,
	//       is_invitation: p.email,
	//       redirect: p.redirect,
	//     },
	//     { auth: true }
	//   );

	//   return resend; // 'SUCCESS: Invitation E-Mail has been sent.'
	// }

	async getStripeSubscription(refresh = false): Promise<SubscriptionObj> {
		if (this.subscription && !refresh) {
			return this.subscription;
		}

		if (this.service?.subs_id) {
			let service_subs_id = this.service?.subs_id;

			// subs_id 가 배열로 올 경우에는 첫번째 값을 사용
			if (Array.isArray(service_subs_id)) {
				service_subs_id = service_subs_id[0];
			}

			let subs_id = service_subs_id.split('#');

			if (subs_id.length < 2) {
				return;
			}

			let SUBSCRIPTION_ID = subs_id[0];

			this.subscription = await skapi.forwardRequest(null, {
				secretName: 'stripe_test',
				url: `https://api.stripe.com/v1/subscriptions/${SUBSCRIPTION_ID}`,
				method: 'GET',
				headers: {
					Authorization: 'Bearer $CLIENT_SECRET',
					'Content-Type': 'application/x-www-form-urlencoded',
				},
			});

		}

		// Apply the subscription/suspension-derived display fields. Extracted so it
		// can be re-run on the REACTIVE instance after the (now non-blocking) Stripe
		// fetch resolves — see applySubscriptionDerived + addServiceToList.
		this.applySubscriptionDerived();

		return this.subscription;
	}

	// Compute the display fields derived from Stripe subscription / suspension
	// state (will_terminate, will_cancel). Kept separate from the fetch so it can
	// be invoked on the reactive instance (currentService / serviceList[id]) once
	// the background Stripe call lands, so the cancel/past-due banners update
	// without project loading having to block on Stripe.
	//
	// Plan policy v2: a cancel no longer suspends (the project moves to Free at
	// the end of the period), so will_terminate means ONLY the earliest deletion
	// of a suspended project, suspended_at + 30 days, and stays '' while a Stripe
	// subscription holds the deletion. The plan label is never overwritten: the
	// old 'Canceled' label hid the real plan and broke the subscription page.
	applySubscriptionDerived(): void {
		const clock = suspensionClock(this.service, Date.now());
		this.service.will_terminate = clock?.at ? dateFormat(clock.at) : '';
		if (this.subscription) {
			const s = this.subscription;
			s.will_cancel =
				this.service.active === 1 && cancelScheduled(s)
					? dateFormat(s.cancel_at * 1000)
					: '';
		}
	}

	async deleteSubscription(): Promise<SubscriptionObj> {
		if (!this.subscription) {
			return null;
		}

		if (this.service?.subs_id) {
			let service_subs_id = this.service?.subs_id;

			if (Array.isArray(service_subs_id)) {
				service_subs_id = service_subs_id[0];
			}

			let subs_id = service_subs_id.split('#');

			if (subs_id.length < 2) {
				return;
			}

			let SUBSCRIPTION_ID = subs_id[0];

			this.subscription = await skapi.forwardRequest(null, {
				secretName: 'stripe_test',
				url: `https://api.stripe.com/v1/subscriptions/${SUBSCRIPTION_ID}`,
				method: 'DELETE',
				headers: {
					Authorization: 'Bearer $CLIENT_SECRET',
				},
				// // data: {
				// //     cancel_at_period_end: true
				// // }
			});

			this.applySubscriptionDerived();
		}
		return this.subscription;
	}

	async cancelSubscription(): Promise<SubscriptionObj> {
		if (!this.subscription) {
			// subscription 이 없다는 것은 subs_id 가 없다는 것, 즉 구독이 없다는 것
			throw 'no subscription';
		}

		if (this.service?.subs_id) {
			let service_subs_id = this.service?.subs_id;

			if (Array.isArray(service_subs_id)) {
				service_subs_id = service_subs_id[0];
			}

			let subs_id = service_subs_id.split('#');

			if (subs_id.length < 2) {
				return;
			}

			let SUBSCRIPTION_ID = subs_id[0];

			this.subscription = await skapi.forwardRequest(null, {
				secretName: 'stripe_test',
				url: `https://api.stripe.com/v1/subscriptions/${SUBSCRIPTION_ID}`,
				method: 'POST',
				headers: {
					Authorization: 'Bearer $CLIENT_SECRET',
				},
				data: {
					cancel_at_period_end: true,
				},
			});

			this.applySubscriptionDerived();
		}

		return this.subscription;
	}

	async setServiceOption(params: {
		prevent_signup?: boolean;
		prevent_inquiry?: boolean; // true 인 경우 sendInquiry API 사용 불가능
		freeze_database?: boolean; // true 이면 데이터 베이스 구조 고정시키는 기능
		prevent_anonymous?: boolean; // true 인 경우 계정이 없는 사용자는 레코드 생성 불가능
		/** BunnyQuery: does the embeddable chat require an account? */
		require_login?: boolean;
		name?: string;
		cors?: string;
		description?: string;
		ai_agent?: string;
		/** the phone verification SMS text; '' puts the built-in default back */
		template_sms_verification?: string;
	}): Promise<ServiceFromServer> {
		let to_update: { [key: string]: any } = {};

		for (let key in params) {
			if (key === 'cors' && params.cors) {
				params.cors = params.cors.trim();
				// if cors have , at the end, remove it
				while (params.cors[params.cors.length - 1] === ',') {
					if (params.cors[params.cors.length - 1] === ',') {
						params.cors = params.cors.slice(0, -1);
						params.cors = params.cors.trim();
					}
				}

				let cors = params.cors.split(',').map((c) => c.trim());
				let service_cors = (this.service.cors || '')
					.split(',')
					.map((c) => c.trim());
				if (
					cors.length === service_cors.length &&
					cors.every((v, i) => v === service_cors[i])
				) {
					return this.service;
				}

				to_update.cors = cors.join(', ');
			} else if (key === 'name' && params.name !== this.service.name) {
				to_update.name = params.name;
			} else if (
				key === 'description' &&
				params.description !== this.service.description
			) {
				to_update.description = params.description;
			} else if (
				params[key as keyof typeof params] !==
				this.service[key as keyof ServiceFromServer]
			) {
				to_update[key] = params[key as keyof typeof params];
			}
		}

		if(Object.keys(to_update).length === 0) {
			return this.service;
		}

		let updated = await skapi.util.request(
			this.admin_private_endpoint + 'service-settings',
			{ service: this.id, owner: this.owner, ...to_update },
			{ auth: true },
		);
		Object.assign(this.service, to_update);
		if (typeof to_update.name === 'string') {
			this.formattedSpecs.serviceStatus.name = to_update.name;
		}
		return updated;
	}

	async registerClientSecret(params: {
		id: string | string[]; // client secret key id. should be array when req is delete
		key?: string; // client secret key value. required when req is create or update
		grp?: number; // access group number. 0 for public, 1 for locked.
		req: 'create' | 'delete' | 'update';
	}): Promise<{
		id: string | string[]; // client secret key id. should be array when req is delete
		key?: string; // client secret key value. required when req is create or update
		grp?: number; // access group number. 0 for public, 1 for locked.
		req: 'create' | 'delete' | 'update';
	}> {
		if (
			params.req === 'delete' &&
			typeof params.id === 'string' &&
			!Array.isArray(params.id)
		) {
			params.id = [params.id];
		}

		await skapi.util.request(
			this.admin_private_endpoint + 'register-client-secret',
			Object.assign(
				{ service: this.id, owner: this.owner },
				skapi.util.extractFormData(params).data || {},
			),
			{ auth: true },
		);

		return params;
	}

	async listClientSecrets(fetchMore: boolean = false, search?: string): Promise<
		DatabaseResponse<{
			grp: number;
			key: string;
			id: string;
		}>
	> {
		let secrets = await skapi.util.request(
			this.admin_private_endpoint + 'register-client-secret',
			{ service: this.id, owner: this.owner, req: 'list', id: search || '' },
			{
				fetchOptions: {
					limit: 120,
					fetchMore: fetchMore,
					ascending: true,
				},
				method: 'post',
				auth: true,
			},
		);
		return secrets;
	}

	async getEmailTemplate(
		params:
			| 'invitation'
			| 'newsletter_subscription'
			| 'verification'
			| 'welcome'
			| 'confirmation',
	): Promise<{
		subject: string;
		url: string;
	}> {
		let updated = await skapi.util.request(
			this.admin_private_endpoint + 'email-templates',
			{ service: this.id, owner: this.owner, template: params },
			{ auth: true },
		);
		return updated;
	}

	async enableService(): Promise<ServiceFromServer> {
		if (this.service.active === 0) {
			this.service.active = 1;
			try {
				await skapi.util.request(
					this.admin_private_endpoint + 'create-service',
					{
						service: this.id,
						owner: this.owner,
						execute: 'enable',
					},
					{ auth: true },
				);
			} catch (err: any) {
				this.service.active = 0;
				throw err;
			}
		}

		return this.service;
	}

	async disableService(): Promise<ServiceFromServer> {
		if (this.service.active > 0) {
			let orgActive = this.service.active;
			this.service.active = 0;

			try {
				await skapi.util.request(
					this.admin_private_endpoint + 'create-service',
					{
						service: this.id,
						owner: this.owner,
						execute: 'disable',
					},
					{ auth: true },
				);
			} catch (err: any) {
				this.service.active = orgActive;
				throw err;
			}
		}

		return this.service;
	}

	async deleteService(): Promise<void> {
		return skapi.util.request(
			this.admin_private_endpoint + 'create-service',
			{
				service: this.id,
				owner: this.owner,
				execute: 'delete',
			},
			{ auth: true },
		);
		// need servicelist removal from outside
	}

	async getMailTemplates(
		params?: {
			searchFor:
				| 'message_id'
				| 'timestamp'
				| 'read'
				| 'complaint'
				| 'subject';
			value: string | number;
			range?: string | number;
			condition?:
				| '>'
				| '>='
				| '='
				| '<'
				| '<='
				| 'gt'
				| 'gte'
				| 'eq'
				| 'lt'
				| 'lte';
			group:
				| 'welcome'
				| 'confirmation'
				| 'verification'
				| 'invitation'
				| 'newsletter_subscription';
		},
		fetchOptions?: { [key: string]: any },
	): Promise<any> {
		try {
			let res = await skapi.util.request(
				this.admin_private_endpoint + 'get-templates',
				Object.assign(
					{
						service: this.id,
						owner: this.owner,
					},
					params,
				),
				{ auth: true, method: 'get', fetchOptions: fetchOptions || {} },
			);

			res.list = res.list.map((value: any) => {
				return {
					message_id: value.mid,
					timestamp: value.stmp,
					subject: value.subj,
					url: value.url,
				};
			});

			return res;
		} catch (err: any) {
			throw err;
		}
	}

	async deleteTemplate(params?: {
		message_id: string;
		group:
			| 'welcome'
			| 'confirmation'
			| 'verification'
			| 'invitation'
			| 'newsletter_subscription';
	}): Promise<any> {
		try {
			return skapi.util.request(
				this.admin_private_endpoint + 'delete-template',
				Object.assign(
					{
						service: this.id,
						owner: this.owner,
					},
					params,
				),
				{ auth: true },
			);
		} catch (err: any) {
			throw err;
		}
	}

	async setTemplate(params?: {
		message_id: string;
		group:
			| 'welcome'
			| 'confirmation'
			| 'verification'
			| 'invitation'
			| 'newsletter_subscription';
	}): Promise<any> {
		try {
			return skapi.util.request(
				this.admin_private_endpoint + 'set-template',
				Object.assign(
					{
						service: this.id,
						owner: this.owner,
					},
					params,
				),
				{ auth: true },
			);
		} catch (err: any) {
			throw err;
		}
	}

	pending: {
		subdomain: false | 'removing' | 'transit' | 'fetching' | string;
		cdn: boolean; // true when cdn refresh is in process
	} = reactive({
		subdomain: false,
		cdn: false,
	});

	subdInfo = reactive({
		srvc: '',
		subd: '',
		ownr: '',
		stat: '',
		['404']: '...',
	});

	getSubdomainInfoTimer: any = null;

	dirInfo: Reactive<{
		cnt: number;
		size: number;
		upl: number;
		path: string;
	}> = reactive({
		cnt: 0,
		size: 0,
		upl: 0,
		path: '',
	});

	dbDirInfo: Reactive<{
		cnt: number;
		size: number;
		upl: number;
		path: string;
	}> = reactive({
		cnt: 0,
		size: 0,
		upl: 0,
		path: '',
	});
	
	async getSubdomainInfo(interval: number = 1000): Promise<{
		srvc: string;
		subd: string;
		ownr: string;
		stat:
			| 'remove'
			| 'change:(subdomain to be changed to)'
			| 'active'
			| string;
		['404']?: string;
	} | null> {
		if (this.getSubdomainInfoTimer) {
			return null;
		}

		this.pending.subdomain = 'fetching';

		let exec = async (): Promise<any> => {
			if (!this.service.service && this.service.subdomain) {
				Object.assign(this.subdInfo, {
					srvc: this.id,
					subd: '',
					ownr: this.owner,
					stat: '',
					['404']: '',
				});

				this.pending.subdomain = false;
				return this.subdInfo;
			}

			let info: any;

			try {
				info = await skapi.util.request(
					this.admin_private_endpoint + 'subdomain-info',
					{ service: this.id, owner: this.owner },
					{ auth: true },
				);
			} catch (err) {
				// retry on error
				interval *= 1.2;
				this.getSubdomainInfoTimer = setTimeout(exec, interval);
				return;
			}

			if (
				typeof info === 'string' ||
				!info ||
				(typeof info === 'object' && Object.keys(info).length === 0)
			) {
				// subdomain removed
				clearTimeout(this.getSubdomainInfoTimer);
				this.getSubdomainInfoTimer = null;
				this.pending.subdomain = false;
				this.service.subdomain = null;

				Object.assign(this.subdInfo, {
					srvc: this.id,
					subd: '',
					ownr: this.owner,
					stat: '',
					['404']: '',
				});

				return this.subdInfo;
			}

			if (info.invid) {
				this.checkCDNStatus(true);
			}

			if (info.stat) {
				// banned | remove | change:(subdomain to be changed to) | active | tracked | needs_refresh
				let pendingStat = info.stat.includes('change:')
					? 'transit'
					: info.stat.includes('remove')
						? 'removing'
						: info.stat === 'active' ||
							  info.stat === 'tracked' ||
							  info.stat === 'needs_refresh' ||
							  info.stat === 'banned'
							? false
							: info.stat;

				this.pending.subdomain = pendingStat;

				if (pendingStat) {
					// is transit or removing - keep polling
					interval *= 1.2;
					this.subdInfo.stat = info.stat;
					this.getSubdomainInfoTimer = setTimeout(exec, interval);
				} else {
					// reached final state
					clearTimeout(this.getSubdomainInfoTimer);
					this.getSubdomainInfoTimer = null;

					this.pending.subdomain = false;
					this.service.subdomain = info.subd;
					if (!info?.['404']) {
						delete this.subdInfo['404'];
					}
					Object.assign(this.subdInfo, info);
					return info;
				}
			} else {
				// no stat in response - retry
				interval *= 1.2;
				this.getSubdomainInfoTimer = setTimeout(exec, interval);
			}
		};

		this.getSubdomainInfoTimer = true; // guard before first async call
		return exec();
	}

	async registerSubdomain(params?: {
		subdomain?: string;
		check?: boolean;
	}): Promise<ServiceFromServer | { status: 'success'; resp: any }> {
		let { subdomain = '', check = false } = params || {};
		this.reserved_key = randomString(16);

		if (this.service?.subdomain === subdomain) {
			return this.service;
		}

		if (this.service?.subdomain) {
			if (subdomain && this.service?.subdomain[0] === '*') {
				throw 'Previous subdomain is in removal process.';
			} else if (subdomain && this.service?.subdomain[0] === '+') {
				throw `Previous subdomain is in transit to "${this.service.subdomain.slice(
					1,
				)}".`;
			}
		}

		// this.pending.subdomain = subdomain ? 'pending' : 'removing';
		// this.subdInfo.stat = subdomain ? 'pending' : 'removing';
		let resp = await skapi.util.request(
			this.admin_private_endpoint + 'register-subdomain',
			{ service: this.id, owner: this.owner, subdomain, check },
			{
				auth: true,
				method: 'post',
			},
		);

		// if (typeof resp !== 'string') {
		// 	this.service.subdomain = resp.subdomain;
		// 	this.subdInfo.stat = 'pending';
		// }

		if (params?.check) {
			return { status: 'success', resp }; // string, { body: string }
		}

		this.service = resp;
		await this.getSubdomainInfo();
		return resp;
	}

	async fetchCDNStatus() {
		return await skapi.util.request(
			this.admin_private_endpoint + 'refresh-cdn',
			{
				service: this.id,
				owner: this.owner,
				exec: 'status',
			},
			{
				auth: true,
				method: 'post',
			},
		);
	}

	cdnPendTimer: any = null;
	checkCDNStatus(immediate?: boolean) {
		if (this.cdnPendTimer) {
			return;
		}

		this.pending.cdn = true;
		let callbackInterval = (immediate = false) => {
			let time = immediate ? 1 : 30000;
			setTimeout(() => {
				this.fetchCDNStatus()
					.then((res) => {
						if (res === 'COMPLETE') {
							this.pending.cdn = false;
							clearTimeout(this.cdnPendTimer);
							this.cdnPendTimer = null;
							this.subdInfo.stat = 'tracked';

							return;
						}
						this.cdnPendTimer = callbackInterval();
					})
					.catch((err) => {
						this.pending.cdn = false;
						clearTimeout(this.cdnPendTimer);
						this.cdnPendTimer = null;
						return;
					});
			}, time);
		};

		this.cdnPendTimer = callbackInterval(immediate);
	}

	async refreshCDN(): Promise<
		| 'IS_QUEUED' // new cdn refresh is queued
		| 'IN_QUEUE' // the previous cdn refresh is still in queue
		| 'IN_PROCESS' // the cdn refresh is in process
	> {
		if (this.pending.cdn) {
			return 'IN_PROCESS';
		}

		if (!this.service.subdomain || this.service.subdomain[0] === '*') {
			throw 'Subdomain does not exists.';
		}

		try {
			this.pending.cdn = true;
			await skapi.util.request(
				this.admin_private_endpoint + 'refresh-cdn',
				{
					service: this.id,
					owner: this.owner,
					exec: 'refresh',
				},
				{
					auth: true,
					method: 'post',
				},
			);

			this.checkCDNStatus();
			return 'IS_QUEUED';
		} catch (err: any) {
			if (err.message === 'Previous cdn refresh is still in queue.') {
				return 'IN_QUEUE';
			}
			if (err.message === 'Previous cdn refresh is in process.') {
				return 'IN_PROCESS';
			} else {
				this.pending.cdn = false;
				if (this.cdnPendTimer) {
					clearTimeout(this.cdnPendTimer);
					this.cdnPendTimer = null;
				}
				throw err;
			}
		}
	}

	async set404(params: {
		path: string; // Set path to file of 404 page. ex) folder/file.html
	}): Promise<'SUCCESS: Removed 404 page'> {
		return await skapi.util.request(
			this.admin_private_endpoint + 'set-404',
			{ service: this.id, owner: this.owner, page404: params.path },
			{ auth: true },
		);
	}

	async uploadHostFiles(
		fileList: FormData | HTMLFormElement | SubmitEvent,
		params: {
			contentTypeMapping?: { [fileName: string]: string };
			prefix?: string;
			target?: 'db' | 'host';
			check_existence?: boolean; // check existence of file before upload, if true, it will skip the file that already exists in the target storage
			// Milliseconds one file's storage POST may take. A file that takes longer is aborted and
			// listed in `failed` with { code: 'TIMEOUT', message: 'Upload timeout: ...' } in `failedErrors`;
			// the next file is then uploaded. Omitted or 0: no limit, as before.
			timeout?: number;
			progress?: (p: {
				status: string; // 'loadstart' | 'progress' | 'abort' | 'error' | 'load' | 'timeout' | 'loadend',
				progress: number;
				currentFile: File;
				completed: File[];
				failed: File[];
				loaded: number;
				total: number;
				abort: () => void;
			}) => void;
		},
	): Promise<{ completed: File[]; failed: File[]; failedErrors: any[]; bin_endpoints: string[] }> {
		let progress = params?.progress || ((p: any) => p);
		const timeout = typeof params?.timeout === 'number' && params.timeout > 0 ? params.timeout : 0;

		if (fileList instanceof SubmitEvent) {
			fileList = fileList.target as HTMLFormElement;
		}

		if (fileList instanceof HTMLFormElement) {
			fileList = new FormData(fileList);
		}

		let reserved_key = this.reserved_key;

		let getSignedParams: Record<string, any> = {
			reserved_key,
			service: this.id,
			owner: this.owner,
			request: params?.target || 'host',
		};

		if (params?.target === 'host') {
			getSignedParams.id = this.service.subdomain;
		}

		if (params?.check_existence) {
			getSignedParams.check_existence = true;
		}

		let xhr: any;
		let fetchProgress = (
			url: string,
			body: FormData,
			progressCallback: (p: ProgressEvent) => void,
			fileName: string,
		) => {
			return new Promise((res, rej) => {
				xhr = new XMLHttpRequest();
				xhr.open('POST', url);
				// One file's whole budget, counted from send() to the end of the response.
				if (timeout > 0) xhr.timeout = timeout;
				xhr.onload = () => {
					let result = xhr.responseText;
					try {
						result = JSON.parse(result);
					} catch (err) {}
					if (xhr.status >= 200 && xhr.status < 300) {
						res(result);
					} else {
						rej(result);
					}
				};
				xhr.onerror = () => rej('Network error');
				xhr.onabort = () => rej('Aborted');
				// Only reachable with a timeout set: an XHR with none never times out. The
				// message keeps the word the retry predicate looks for (isRetriableUploadError).
				xhr.ontimeout = () =>
					rej({ code: 'TIMEOUT', message: `Upload timeout: "${fileName}" was not finished within ${timeout} ms.` });

				// xhr.addEventListener('error', rej);
				if (xhr.upload && typeof progressCallback === 'function') {
					xhr.upload.onprogress = progressCallback;
				}

				xhr.onprogress = progressCallback;

				xhr.send(body);
			});
		};

		let completed = [];
		let failed = [];
		// The real per-file upload error, parallel to `failed`. Previously the
		// S3 POST rejection was discarded, so a failed chatbox upload surfaced as
		// a bare "Upload failed" with no cause, undiagnosable (e.g. the one .xls
		// that "won't upload no matter what"). Callers can now report the reason.
		let failedErrors: any[] = [];
		let bin_endpoints = [];
		let prefix = params.prefix ? params.prefix : '';
		prefix =
			prefix && prefix[prefix.length - 1] === '/' ? prefix : prefix + '/';

		for (let [key, f] of (fileList as any).entries()) {
			if (!(f instanceof File)) {
				continue;
			}

			// Safety net. Callers compose at capture (see normalizeStoragePath), but this is the
			// one place every upload funnels through, so compose here too rather than trust that
			// every present and future caller remembered.
			let rawName = normalizeStoragePath(f.webkitRelativePath || f.name);
			let fn = rawName.split('/');
			let fns = fn[fn.length - 1];
			// Look the Content-Type up under BOTH forms. The mapping is built by the caller from
			// its own copy of the name; if that copy is decomposed and this one is composed, a
			// single-form lookup silently drops the Content-Type for every non-ASCII filename and
			// the file is served as application/octet-stream.
			let ct =
				params?.contentTypeMapping?.[fns] ||
				params?.contentTypeMapping?.[fns.normalize('NFD')] ||
				null;

			let signedParams = Object.assign(
				{
					key: prefix + rawName,
					size: f.size || 0,
					contentType: f.type || ct,
				},
				getSignedParams,
			);

			let { fields = null, url } = await skapi.util.request(
				this.record_private_endpoint + 'get-signed-url',
				signedParams,
				{ auth: true },
			);

			bin_endpoints.push(url);

			let form = new FormData();

			for (let name in fields) {
				form.append(name, fields[name]);
			}

			form.append('file', f);

			try {
				await fetchProgress(url, form, (p: ProgressEvent) =>
					progress({
						status: p.type,
						progress: (p.loaded / p.total) * 100,
						currentFile: f,
						completed,
						failed,
						loaded: p.loaded,
						total: p.total,
						abort: () => xhr.abort(),
					}),
					rawName,
				);
				completed.push(f);
			} catch (err) {
				failed.push(f);
				failedErrors.push(err);
			}
		}

		return { completed, failed, failedErrors, bin_endpoints };
	}

	async deleteHostFiles(params: {
		target?: 'db' | 'host';
		paths: string[]; // path without subdomain ex) folder/file.html
	}): Promise<string> {
		if (!params.paths || !params.paths.length) return;
		return skapi.util.request(
			this.record_private_endpoint + 'del-files',
			{
				service: this.id,
				owner: this.owner,
				endpoints: params.paths,
				reserved_key: this.reserved_key,
				storage: params.target || 'host',
			},
			{ auth: true, method: 'post' },
		);
	}

	async getDirInfo(params?: { target: 'db' | 'host' }): Promise<{
		name: string; // !
		path: string; // subdomain
		size: number;
		upl: number;
		cnt: number;
	}> {
		let dirInfo = await skapi.util.request(
			this.admin_private_endpoint + 'host-directory',
			Object.assign(
				{ service: this.id, owner: this.owner },
				{ dir: '', info: true, target: params?.target || 'host' },
			),
			{
				fetchOptions: {
					limit: 1,
					fetchMore: false,
					ascending: true,
				},
				method: 'post',
				auth: true,
			},
		);

		if (dirInfo) {
			if (params?.target === 'db') {
				Object.assign(this.dbDirInfo, dirInfo);
			} else {
				Object.assign(this.dirInfo, dirInfo);
			}
		}
		return dirInfo;
	}

	async listHostDirectory(
		params: {
			dir: string; // unix style dir with subdomain. ex) subdomain/folder/subfolder
			target?: 'db' | 'host';
		},
		fetchMore: boolean = false,
		limit: number = 120,
	): Promise<{
		[key: string]: any;
		list: {
			name: string; // file name file.txt | #folder
			path: string; // file path ex) subdomain/folder/subfolder
			size: number;
			upl: number;
			cnt: number;
		}[];
	}> {
		let _subd = this.service?.subdomain;
		if (_subd && (_subd[0] === '*' || _subd[0] === '+')) {
			_subd = _subd.slice(1);
		}

		let dirList = await skapi.util.request(
			this.admin_private_endpoint + 'host-directory',
			Object.assign({ service: this.id, owner: this.owner }, params),
			{
				fetchOptions: {
					limit,
					fetchMore: !!fetchMore,
					ascending: true,
				},
				method: 'post',
				auth: true,
			},
		);

		// Hide system-managed db folders (e.g. the MCP export namespace) from the
		// user's file browser. Filtered HERE because this is the single choke
		// point every db consumer goes through — the browser listing AND the
		// recursive "reindex all" walk — so neither can see or descend into them.
		// `rawCount` is preserved so callers can tell "the server returned nothing"
		// (real end of directory) from "everything on this page was filtered out",
		// which would otherwise falsely terminate their paging loop.
		// The host target (subdomain hosting) is deliberately untouched.
		if (params?.target === 'db' && Array.isArray(dirList?.list)) {
			dirList.rawCount = dirList.list.length;
			dirList.list = dirList.list.filter((i: any) => !isReservedDbEntry(i?.name));
		}
		return dirList;
	}

	async getTemporaryUrl(params: {
		request: 'get-host' | 'get-db' | 'get-db-zip' | 'get-host-zip';
		path: string;
		contentType?: string;
		expires: number; // seconds
		generate_redirect_url?: boolean; // generate redirect url after upload, if true, it will return the url of the uploaded file in the response
		generate_temporary_cdn_url?: boolean; // generate temporary cdn url for the uploaded file, if true, it will return the cdn url in the response
		// Seconds the browser may reuse this mint. Switches the request to a
		// CACHEABLE GET, so the same signed url comes back without a round trip
		// and the body already in the browser cache stays addressable. Without
		// it every mint is a fresh SigV4 url, i.e. a fresh cache key, and the
		// file downloads again every time. The url's own lifetime is still
		// `expires`; what keeps the file available past that is the cached body.
		browser_cache?: number;
		// Bypass the cached mint above and force a fresh signed url.
		refresh?: boolean;
	}): Promise<{ url: string }> {
		let request = params.request || 'get-host';
		let key = params.path || '';
		let expires = params.expires || 3600;
		let contentType = params.contentType || 'application/octet-stream';
		let generate_redirect_url = params.generate_redirect_url || false;
		let generate_temporary_cdn_url = params.generate_temporary_cdn_url || false;
		// set contentType based on file extension if contentType is not provided
		if (!params.contentType) {
			contentType = mime.getType(key) || contentType;
		}

		// A cacheable mint has to be a GET: a POST response is not stored by any
		// browser cache, so the same signed url could never come back without a
		// round trip. Only applies to the presign branch; a cdn url is already
		// stable by construction.
		const browser_cache =
			!generate_temporary_cdn_url && params.browser_cache ? params.browser_cache : 0;
		const cacheParams: Record<string, any> = {};

		if (browser_cache) {
			cacheParams.browser_cache = browser_cache;
			// Partitions the browser cache per user. A cache is keyed by url
			// alone and shared by every user of the profile, so without this a
			// second user signing in would be served the first user's signed
			// urls. NOTE this.owner is the PROJECT owner, not the viewer, so it
			// cannot stand in here. The backend rejects a uid that is not the
			// caller, so a stale value fails loudly rather than silently.
			const uid = (skapi as any).user?.user_id;
			if (uid) cacheParams.uid = uid;
			// Cache generation, plus a window stamp when this is a repair. Derived
			// HERE rather than taken from the caller so no second call site can
			// forget it and silently get the un-bustable mint back. See the engine's
			// previewMintCacheToken for why a query parameter and not a header.
			cacheParams.nocache = previewMintCacheToken(params.refresh);
		}

		return skapi.util
			.request(
				this.record_private_endpoint + 'get-signed-url',
				Object.assign(
					{ service: this.id, owner: this.owner },
					{
						request,
						key,
						expires,
						contentType,
						generate_redirect_url,
						generate_temporary_cdn_url,
					},
					cacheParams,
				),
				{
					auth: true,
					method: browser_cache ? 'get' : 'post',
					// NO `revalidate`. It sends `Cache-Control: no-cache` as a REQUEST
					// header, which is not CORS-safelisted, and the record gateway's
					// preflight does not allow it (checked against the live api), so the
					// browser refused to send the repair at all and the preview died as
					// unavailable. The `nocache` value above does the same job from
					// inside the url, where nothing can veto it.
				},
			)
			.then((tempUrl: any) => {
				let filename = key.split('/').pop();
				if(filename[0] === '#') {
					filename = filename.slice(1) + '.zip';
				}
				if (request === 'get-db-zip' || request === 'get-host-zip') {
					if (tempUrl?.tmp_id) {
						let tempId = tempUrl.tmp_id;
						return this.fetchTmpZip({ key: tempId, expires, filename });
					}
				}
				// A suspended or contained project answers a platform account or a super
				// admin with a plain presigned url instead of a redirect or CDN url, so
				// that the owner can still download while the edge refuses everyone else.
				// It is already absolute: prefixing it would 404.
				if (tempUrl?.absolute) {
					return tempUrl;
				}
				if(generate_redirect_url) {
					tempUrl.url = this.record_public_endpoint + tempUrl.url;
				}
				else if(generate_temporary_cdn_url) {
					tempUrl.url = `https://db.${hostDomain}/` + tempUrl.url;
				}
				return tempUrl;
			});
	}

	async fetchTmpZip(params: {
		key: string;
		expires: number;
		filename: string;
	}): Promise<{ url: string }> {
		try {
			let resp = await skapi.util.request(
				this.record_private_endpoint + 'get-signed-url',
				Object.assign(
					{ service: this.id, owner: this.owner },
					{
						request: 'get-tmp',
						key: params.key,
						expires: params.expires,
						contentType: mime.getType(params.filename) || 'application/zip',
						filename: params.filename,
					},
				),
				{ auth: true, method: 'post' },
			);
			return resp;
		} catch (err) {
			if (err instanceof SkapiError && err.code === 'NOT_EXISTS') {
				// recurring check until the zip file is ready, with 5 seconds interval
				await new Promise((res) => setTimeout(res, 5000));
				return this.fetchTmpZip(params);
			} else {
				throw err;
			}
		}
	}
	// -------------------------------- vivians area --------------------------------

	async registerNewsletterGroup(params: {
		group: string;
		restriction: number;
	}): Promise<string> {
		Object.assign(params, { service: this.id, owner: this.owner });
		return skapi.util.request(
			this.admin_private_endpoint + 'register-newsletter-group',
			params,
			{ auth: true },
		);
	}

	async newsletterGroupEndpoint() {
		let response = await skapi.util.request(
			this.admin_private_endpoint + 'newsletter-group-endpoint',
			{ service: this.id, owner: this.owner },
			{ auth: true },
		);

		return response;
	}

	// ------------------------------- vivians area -------------------

	async refresh(onlyServiceInfo = false): Promise<ServiceFromServer> {
		let service = await skapi.util.request(
			this.admin_private_endpoint + 'pull-services',
			{
				service: skapi.service,
				owner: skapi.owner,
				service_id: this.id,
				owner_id: this.owner,
				bypass_cached_service: true,
			},
			{ auth: true },
		);
		for (let region in service) {
			if (!Array.isArray(service[region])) {
				continue;
			}

			// Each region maps to an array of matching services, optionally
			// followed by skapi's 'end' startKey marker; empty regions come
			// back as ['end']. Pick the first real (object) entry rather than
			// blindly taking index 0, which may be the 'end' string.
			const fresh = service[region].find(
				(entry: any) => entry && typeof entry === 'object',
			);
			if (fresh) {
				// The API response may omit fields that were either set
				// client-side (projectId) or are sparse-stored
				// server-side (ai_agent). Preserve them so reactive
				// computeds that read these fields don't flicker false.
				if (!fresh.ai_agent && this.service?.ai_agent) {
					fresh.ai_agent = this.service.ai_agent;
				}
				if (!fresh.projectId && this.service?.projectId) {
					fresh.projectId = this.service.projectId;
				}
				this.service = fresh;
				break;
			}
		}

		if (onlyServiceInfo) {
			return this.service;
		}

		this.formattedSpecs = formatSpecs(this.service);

		let pendingStat =
			this.service.subdomain[0] === '+'
				? 'transit'
				: this.service.subdomain[0] === '*'
					? 'removing'
					: false;
		this.pending.subdomain = pendingStat;

		this.getSubdomainInfo();

		this._subsPromise = this.getStripeSubscription();

		return this.service;
	}

	static async load(srvc: ServiceFromServer): Promise<Service> {
		if (srvc && typeof srvc === 'object') {
			let {
				admin_private_endpoint,
				admin_public_endpoint,
				record_private_endpoint,
				record_public_endpoint,
			} = await ENDPOINTS;

			let inst = new Service(srvc, [
				admin_private_endpoint,
				record_private_endpoint,
				admin_public_endpoint,
				record_public_endpoint,
			]);

			// NON-BLOCKING: the project's own info is fully built by the ctor above.
			// The per-project Stripe subscription (_subsPromise, fired in the ctor)
			// is a RELATED-data fetch — don't block the project load (and the whole
			// list's Promise.all) on it. It resolves in the background; the
			// subscription-derived UI is re-applied reactively in addServiceToList.
			// (Also more robust: a Stripe error no longer fails the project load.)

			return inst;
		} else {
			throw new Error('Invalid service object.');
		}
	}
}

export async function createService(params: {
	name: string;
	description?: string;
	/**
	 * Where the project's data lives. Optional: left out, it falls back to the
	 * region closest to the visitor, which is what the picker preselects.
	 * A project can never be moved -- its region is the first four characters
	 * of the service id.
	 */
	region?: string;
}): Promise<ServiceFromServer> {
	if (!params?.name) throw new Error('Invalid service name.');

	let { admin_private_endpoint } = await ENDPOINTS;

	// A picked region only counts if this environment actually offers it; the
	// backend rejects anything else outright ("Region is not yet supported").
	let serviceRegion = resolveRegion(params.region, skapi.connection?.locale);

	let payload: { [key: string]: any } = {
		name: params.name,
		service: skapi.service,
		owner: skapi.owner,
		execute: 'create',
		region: serviceRegion,
		spec: 'BunnyQuery',
	};

	if (params.description) {
		payload.description = params.description;
	}

	let service = await skapi.util.request(admin_private_endpoint + 'create-service', payload, {
		auth: true,
	});

	return service as ServiceFromServer;
}
